import React from "react";
import "./DateSeparator.scss";
import { useTranslation } from "react-i18next";

type DateSeparatorProps = {
  date: Date;
};

/**
 * DateSeparator component displays a date header to separate messages by day
 * Using the project's theme styles similar to modern chat applications
 */
const DateSeparator: React.FC<DateSeparatorProps> = ({ date }) => {
  const { t } = useTranslation();
  const formatDate = (date: Date): string => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    const messageDate = new Date(date);
    const weekAgo = new Date(today);
    weekAgo.setDate(today.getDate() - 7);

    // Reset time to compare only dates
    today.setHours(0, 0, 0, 0);
    yesterday.setHours(0, 0, 0, 0);
    messageDate.setHours(0, 0, 0, 0);
    weekAgo.setHours(0, 0, 0, 0);

    if (messageDate.getTime() === today.getTime()) {
      return t("US.COLLECTION.COMMON:COMMON.TODAY");
    } else if (messageDate.getTime() === yesterday.getTime()) {
      return t("US.COLLECTION.COMMON:COMMON.YESTERDAY");
    } else if (messageDate.getTime() > weekAgo.getTime()) {
      // Show day of week for recent dates (e.g., "Monday", "Tuesday")
      return messageDate.toLocaleDateString("en-US", {
        weekday: "long",
      });
    } else if (messageDate.getFullYear() === today.getFullYear()) {
      // Show month and day for same year (e.g., "July 21")
      return messageDate.toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
      });
    } else {
      // Show full date for different year (e.g., "July 21, 2024")
      return messageDate.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    }
  };

  return (
    <div className="date-separator">
      <div className="date-separator-line"></div>
      <div className="date-separator-text">{formatDate(date)}</div>
      <div className="date-separator-line"></div>
    </div>
  );
};

export default DateSeparator;
